// @ts-check
import { CertQualificationUtils } from "../../shared/certQualificationUtils";
import { formatCertificationIcons } from "../../component-templates/certification-icon/utils";
import { getSplitAttributes } from "../../shared/split";
import { QUALIFICATION } from "../../component-templates/certification-badge/constants.js";
/**
 * @typedef {import('../../component-templates/certification-icon/utils.js').Certification} Certification
 * @typedef {import('../../components/roster/types.js').UserCertification} UserCertification
 * @typedef {import("../../shared/users").ProcessedUserQualification} ProcessedUserQualification
 */

export const backgroundCheckKeyName = 'Background Check';
/**
 * Service class for fetching user certifications. To get user certifications, it requires
 * a call to CM backend to get the certifications for a user. It then filters the certifications.
 * To avoid multiple calls for the same user, it'll cache the certifications for a user.
 * The next time that user's certifications are requested, it'll return the cached certifications instead.
 */
export default class RosterUserCertificationService {
  /* @ngInject */
  constructor(
    Auth,
    Companies,
    Users, 
    /** @type {import('../../shared/users').UsersModule} */
    UsersService
  ) {
    this.Auth = Auth;
    this.companies = Companies;
    this.users = Users;
    this.UsersService = UsersService;

    this.userCertifications = {};
    this.userFilteredCerts = {};

    // Get splits related to user certs
    this.Auth.splitClient.ready().then(() => {
      const attributes = getSplitAttributes(this.Auth);
      this.drugScreenSplitOn = this.Auth.splitClient.getTreatment('drug_screening_p0', attributes) === 'on';
      this.advancedBackgroundCheckSplitOn = this.Auth.splitClient.getTreatment('advanced_background_check_p0', attributes) === 'on';
    });
    // Get company screening requirements
    this.companies.getCompanyData().then((companyData) => {
      this.companyDrugScreenRequirement = companyData.drug_screen_required;
      this.companyAdvancedBackgroundCheckRequirement = companyData.background_check_required;
    });
  }

  /**
   * @param {string} externalId of the selected user
   * @returns {Promise<UserCertification[]>}
   */
  async getUserCertifications(externalId) {
    if (this.userCertifications[externalId] === undefined) {
      await this._fetchUserCertifications(externalId).then((certs) => {
        this.userCertifications[externalId] = certs;
        this.userFilteredCerts[externalId] = formatCertificationIcons(certs);
        return certs;
      });
    }
    return this.userCertifications[externalId];
  }

  /**
   * @param {ProcessedUserQualification[]} backgroundCheckQualifications
   * @returns {ProcessedUserQualification|null}
   *
   * Returns the background check certification that matches the active company's advanced background check
   * requirement. If the user does not have the required certification, it returns null.
   */
  _getMatchingBackgroundCheckQualification(backgroundCheckQualifications) {
    if (!this.advancedBackgroundCheckSplitOn) return null;
    if (backgroundCheckQualifications.length === 0) return null;

    const sortedBackgroundCheckQualifications = CertQualificationUtils.getHighestBackgroundCheckQualification(backgroundCheckQualifications);
    if (CertQualificationUtils.getBackgroundCheckLevel(sortedBackgroundCheckQualifications) < this.companyAdvancedBackgroundCheckRequirement) return null;
    return sortedBackgroundCheckQualifications;
  }

  /**
   * @param {ProcessedUserQualification[]} drugScreenQualifications
   * @returns {ProcessedUserQualification|null}
   *
   * Returns the drug screening certification that matches the active company's drug screening requirement.
   * If the user does not have the required certification, it returns null.
   */
  _getMatchingDrugScreenQualification(drugScreenQualifications) {
    if (!this.drugScreenSplitOn) return null; 
    if (drugScreenQualifications.length === 0) return null;

    const sortedDrugScreenQualifications = CertQualificationUtils.getHighestDrugScreeningQualification(drugScreenQualifications);
    if (CertQualificationUtils.getDrugScreenLevel(sortedDrugScreenQualifications) < this.companyDrugScreenRequirement) return null;
    return sortedDrugScreenQualifications;
  }

  /**
   * Fetches the user's qualifications as certifications
   * 
   * @param {string} externalId of the selected user
   * @returns {Promise<Certification[]>}
   */
  async _fetchUserCertifications(externalId) {
    const verifiedCerts = [];
  
      await this.UsersService.getUserQualifications(externalId)
        .then(qualifications => {
          const backgroundCheckQualifications = qualifications.filter(qualification => qualification.qualificationType === QUALIFICATION.TYPES.BACKGROUND_CHECK)
          const drugScreenQualifications = qualifications.filter(qualification => qualification.qualificationType === QUALIFICATION.TYPES.DRUG_SCREEN)

          const matchingBackgroundCheckQualification = this._getMatchingBackgroundCheckQualification(backgroundCheckQualifications)
          const matchingDrugScreenQualification = this._getMatchingDrugScreenQualification(drugScreenQualifications)

          if (matchingBackgroundCheckQualification) {
            verifiedCerts.push(RosterUserCertificationService.qualificationToCertification(matchingBackgroundCheckQualification))
          }
          if (matchingDrugScreenQualification) {
            verifiedCerts.push(RosterUserCertificationService.qualificationToCertification(matchingDrugScreenQualification))
          }

          return verifiedCerts;
        })
        .catch(error => console.error('Error fetching background checks and drug screens:', error))
    return verifiedCerts;
  }

  /**
   * @returns {Certification}
   */
  static qualificationToCertification(qualification) {
    return {
        badgeIcon: qualification.icon,
        certificateId: qualification.qualificationId.toString(),
        certificateName: qualification.qualificationName,
        certificateUrl: null,
        completedOn: qualification.verification.verifiedAt,
        expiresOn: qualification.expiresOn,
        notes: null,
        verified: true,
        verifiedBy: qualification.verification.verifiedBy,
      };
  }
}

RosterUserCertificationService.$inject = ['Auth', 'Companies', 'Users', 'UsersService'];